/**
 * @file StockLevelsList.tsx
 * @description Остатки компактным списком до 1024 (phase19.31, SC59; заменил
 * карточки 200–230px, на 360 помещалось 2–3 партии).
 *
 * Строка ~72px: первая линия — название и количество, вторая — «партия ·
 * ячейка · срок» (+ склад, если складов несколько, и резерв, если он есть).
 * «без места» и «срок не указан» — янтарным. Галочка отбора — слева (зона
 * нажатия по высоте кнопки темы), «⋯» — справа.
 *
 * В отборе по этикеткам — статус этикеток партии третьей линией и степпер
 * количества у отмеченной строки (phase19.22, W17-9c, W20).
 */

import { Fragment, type ReactNode } from "react";

import { StockSelectCell } from "./StockLevelCells";
import { formatStockQty, stockExpiryView, stockLotShort, type StockRowViewProps } from "./stock-level-format";
import { LabelQtyStepper } from "../labels/LabelQtyStepper";
import { LabelStatusBadge } from "../labels/LabelStatusBadge";
import { LIST_FRAME_CLASS } from "../../ui/ListStates";
import { formatSkuName, type StockLevelDto } from "../../../lib/api-wms";
import { cn } from "@/lib/utils";

/** Части второй линии: «RCP-…018-1 · без места · до 21.09.27». */
function metaParts(level: StockLevelDto, showWarehouse: boolean): ReactNode[] {
  const expiry = stockExpiryView(level);
  const parts: ReactNode[] = [
    <span key="lot" className="font-mono" title={level.lot?.batchNumber}>
      {stockLotShort(level)}
    </span>,
  ];
  if (level.lot?.bin) parts.push(<span key="bin" className="font-mono">{level.lot.bin.code}</span>);
  else if (level.lot) parts.push(<span key="bin" className="text-warning">без места</span>);
  if (expiry.short) {
    parts.push(
      <span key="exp" className={expiry.tone === "warning" ? "text-warning" : undefined}>
        {expiry.short}
      </span>,
    );
  }
  if (showWarehouse) parts.push(<span key="wh">{level.warehouse.name}</span>);
  if (Number(level.totalReserved) > 0) parts.push(<span key="res">резерв {formatStockQty(level.totalReserved)}</span>);
  return parts;
}

export function StockLevelsList({
  items,
  selectable,
  isSelected,
  onToggle,
  actions,
  selectState,
  labelsMode,
  qtyOf,
  onQty,
  showWarehouse,
}: StockRowViewProps) {
  return (
    <ul aria-label="Остатки по партиям" className={cn(LIST_FRAME_CLASS, "divide-y divide-[var(--border)]")}>
      {items.map((level) => {
        const selected = selectable && isSelected(level);
        const name = formatSkuName(level.sku);
        return (
          <li
            key={level.id}
            data-state={selected ? "selected" : undefined}
            className={cn("flex items-center gap-1 pr-2", selectable ? "pl-1" : "pl-3", selected && "bg-[var(--row-selected,var(--accent))]")}
          >
            {selectable ? (
              <StockSelectCell level={level} checked={isSelected(level)} onToggle={onToggle} state={selectState?.(level)} />
            ) : null}
            <div className="flex min-h-18 min-w-0 flex-1 flex-col justify-center gap-0.5 py-2">
              <span className="flex min-w-0 items-baseline justify-between gap-2">
                <span className="type-body min-w-0 truncate font-medium text-on-surface" title={name}>
                  {name}
                </span>
                <span className="type-body shrink-0 font-semibold tabular-nums">{formatStockQty(level.quantity)}</span>
              </span>
              <span className="type-caption min-w-0 truncate text-on-surface-variant">
                {metaParts(level, showWarehouse).map((part, index) => (
                  <Fragment key={index}>
                    {index > 0 ? " · " : null}
                    {part}
                  </Fragment>
                ))}
              </span>
              {labelsMode ? (
                <span className="flex flex-wrap items-center gap-2 pt-1">
                  <LabelStatusBadge status={level.lot?.labelStatus} printedAt={level.lot?.labelsPrintedAt} />
                  {selected && qtyOf && onQty ? (
                    <LabelQtyStepper value={qtyOf(level)} onChange={(qty) => onQty(level, qty)} ariaLabel={`Этикеток: ${name}`} />
                  ) : null}
                </span>
              ) : null}
            </div>
            <div className="shrink-0">{actions(level)}</div>
          </li>
        );
      })}
    </ul>
  );
}
